/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { scroller } from 'react-scroll';

import { Link, Typography2 } from '@coursera/cds-core';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import useIntersectionObserver from 'bundles/tutorials/hooks/useIntersectionObserver';

import _t from 'i18n!nls/tutorials';

const styles = {
  container: css`
    padding: 10px;
  `,
  header: css`
    margin-bottom: 1rem;
  `,
  headingList: css`
    padding-left: 0;
  `,
  heading: css`
    padding: 4px 0;
    list-style: none;
    color: black;
  `,
  link: css`
    color: black;

    &:hover,
    &:visited {
      color: black !important;
    }

    &.active {
      color: var(--cds-color-blue-700) !important;
      text-decoration: none;
    }
  `,
};

type TableOfContentLink = {
  headingId: string;
  label: string;
};

export type Props = {
  links: Array<TableOfContentLink>;
};

const TableOfContents = ({ links }: Props) => {
  const [activeHeadingId, setActiveHeadingId] = useState(links[0].headingId);
  useIntersectionObserver(setActiveHeadingId);

  if (!links.length) {
    return null;
  }

  const handleNavigation = (headingId: string) => {
    scroller.scrollTo(headingId, {
      offset: -70,
      smooth: true,
    });
  };

  return (
    <nav className="rc-TableOfContents" css={styles.container}>
      <Typography2 component="h3" variant="subtitleMedium" css={styles.header}>
        {_t('On this page')}
      </Typography2>
      <ul css={styles.headingList}>
        {links.map((link) => (
          <li key={link.headingId} css={styles.heading}>
            <Link
              className={activeHeadingId === link.headingId ? 'active' : ''}
              css={styles.link}
              variant="quiet"
              typographyVariant="bodyPrimary"
              href={`#${link.headingId}`}
              component={TrackedA}
              trackingName="toc_link"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                handleNavigation(link.headingId);
                setActiveHeadingId(link.headingId);
              }}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default TableOfContents;
